import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60 * 1000;  // 60s

// Singleton-ish hook — multiple components mounting it will each poll;
// since this is only used in AppRoutes once, that's fine.
export function useMaintenanceStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let timer = null;

    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/maintenance/status`, {
          headers: { 'Cache-Control': 'no-cache' },
          timeout: 6000,
        });
        if (!mountedRef.current) return;
        setStatus(res.data?.data || null);
      } catch {
        // Silent — keep last known status; if we never got one, default to all-OK.
        if (!mountedRef.current) return;
        setStatus((prev) => prev || { customer: { active: false }, pickup_man: { active: false } });
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    fetchStatus();
    timer = setInterval(fetchStatus, POLL_MS);

    // Force an immediate re-check when any API call returns 503 MAINTENANCE_ACTIVE.
    const onMaintenanceEvent = (e) => {
      const role = e?.detail?.role;
      // Optimistically flip the relevant role to active so UI re-renders without waiting for HTTP.
      if (role === 'customer' || role === 'pickup_man') {
        setStatus((prev) => ({
          ...(prev || { customer: { active: false }, pickup_man: { active: false } }),
          [role]: {
            active: true,
            start_at: e.detail.start_at || null,
            end_at: e.detail.end_at || null,
            message: e.detail.detail || null,
          },
        }));
      }
      fetchStatus();  // also refresh authoritative state in the background
    };
    window.addEventListener('maintenance-active', onMaintenanceEvent);

    return () => {
      mountedRef.current = false;
      if (timer) clearInterval(timer);
      window.removeEventListener('maintenance-active', onMaintenanceEvent);
    };
  }, []);

  return { status, loading };
}
